import { html } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { LitElement } from 'lit'
import { CSCDeliveryMatchMethod } from './delivery-match-method.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { consume } from '@lit/context'
import { style } from './delivery-match-delivery-method-selector.style.js'
import { DeliveryMatchDeliveryMethodsContext, type DeliveryMatchDeliveryMethodsContextType } from './provider.js'
import type { DeliveryMethod } from './api/shipment-methods.js'
import { DEFAULT_VISIBLE_METHODS_ON_INIT } from './visible-methods-on-init.js'

@customElement('csc-delivery-match-delivery-method-selector')
export class CSCDeliveryMatchDeliveryMethodSelector extends LitElement {
  private showRadioButtonsMethods: boolean
  private visibleMethodsOnInit: number

  private static readonly BATCH_SIZE = 5

  static override styles = [style]

  @consume({ context: DeliveryMatchDeliveryMethodsContext, subscribe: true })
  private deliveryMethods!: DeliveryMatchDeliveryMethodsContextType

  @state()
  private nextBatch: number = CSCDeliveryMatchDeliveryMethodSelector.BATCH_SIZE

  private methodElementCache = new Map<string, CSCDeliveryMatchMethod>()

  constructor({ showRadioButtonsMethods, visibleMethodsOnInit }: { showRadioButtonsMethods?: boolean; visibleMethodsOnInit?: number }) {
    super()
    this.showRadioButtonsMethods = showRadioButtonsMethods ?? false
    this.visibleMethodsOnInit = visibleMethodsOnInit ?? DEFAULT_VISIBLE_METHODS_ON_INIT
  }

  private handleVisibleCountChange = (event: CustomEvent<{ visible: number; total: number; nextBatch: number }>) => {
    if (this.nextBatch === event.detail.nextBatch) return
    this.nextBatch = event.detail.nextBatch
  }

  private getMethodElement(method: DeliveryMethod): CSCDeliveryMatchMethod {
    const cached = this.methodElementCache.get(method.methodId)
    if (cached) {
      cached.method = method
      cached.showRadioButton = this.showRadioButtonsMethods
      cached.requestUpdate()
      return cached
    }
    const element = new CSCDeliveryMatchMethod({ method, showRadioButton: this.showRadioButtonsMethods })
    this.methodElementCache.set(method.methodId, element)
    return element
  }

  override render() {
    return html`<cse-paper class="delivery-match-delivery-selector">
      <cse-text variant="heading-3" class="delivery-match-title">
      ${t('delivery_methods_title', { ns: 'csc-delivery-match', default: 'Choose your delivery time' })}
      </cse-text>
      <cse-expandable
        variant="list-incremental"
        visible-on-init="${this.visibleMethodsOnInit}"
        batch-size="${CSCDeliveryMatchDeliveryMethodSelector.BATCH_SIZE}"
        @visible-count-change=${this.handleVisibleCountChange}
      >
        <cse-link slot="toggle-open">
          ${t('show_more_delivery', {
            ns: 'csc-delivery-match',
            default: 'Show {{variable_count}} more',
            variable_count: this.nextBatch,
          })}
        </cse-link>
        <cse-group direction="column">
          ${this.deliveryMethods.map((method) => this.getMethodElement(method))}
        </cse-group>
      </cse-expandable>
    </cse-paper>`
  }
}
